/**
 * Product Add-ons Component
 * Handles loading complementary products and adding them to cart with the main product.
 */

if (!customElements.get('product-addons')) {
  customElements.define(
    'product-addons',
    class ProductAddons extends HTMLElement {
      constructor() {
        super();
        this.loaded = false;
      }

      connectedCallback() {
        // Load immediately - the block is in the product info area which is always visible
        this.loadRecommendations();
      }

      loadRecommendations() {
        if (this.loaded) return;
        this.loaded = true;

        const url = this.dataset.url;
        if (!url) return;

        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            const html = document.createElement('div');
            html.innerHTML = text;
            const recommendations = html.querySelector('product-addons');
            if (recommendations && recommendations.innerHTML.trim().length) {
              this.innerHTML = recommendations.innerHTML;
            }
          })
          .catch((e) => {
            console.error('Failed to load product add-ons:', e);
          });
      }

      /**
       * Get all selected add-on variant IDs
       * @returns {Array<{id: string, quantity: number}>}
       */
      getSelectedAddons() {
        const checkboxes = this.querySelectorAll('.fs-addon-checkbox:checked');
        return Array.from(checkboxes).map((checkbox) => ({
          id: checkbox.dataset.variantId,
          quantity: 1,
        }));
      }

      /**
       * Check if any add-ons are selected
       * @returns {boolean}
       */
      hasSelectedAddons() {
        return this.querySelectorAll('.fs-addon-checkbox:checked').length > 0;
      }

      /**
       * Clear all selected add-ons
       */
      clearSelection() {
        this.querySelectorAll('.fs-addon-checkbox:checked').forEach((checkbox) => {
          checkbox.checked = false;
        });
      }
    }
  );
}

/**
 * Extend the product-form to handle add-ons
 * This patches the existing product-form's submit handler
 */
(function () {
  // Wait for product-form to be defined
  const waitForProductForm = setInterval(() => {
    if (customElements.get('product-form')) {
      clearInterval(waitForProductForm);
      patchProductForm();
    }
  }, 100);

  function patchProductForm() {
    // Get the original class
    const OriginalProductForm = customElements.get('product-form');

    // Create a new class that extends the original
    class ProductFormWithAddons extends OriginalProductForm {
      constructor() {
        super();
      }

      /**
       * Get the product-addons element if it exists
       */
      getProductAddons() {
        // Look within product-info first, then the whole document
        const productInfo = this.closest('product-info');
        if (productInfo) {
          return productInfo.querySelector('product-addons');
        }
        return document.querySelector('product-addons');
      }

      /**
       * Override addSingleProduct to handle add-ons
       */
      addSingleProduct(formData, config, evt) {
        const productAddons = this.getProductAddons();
        const addons = productAddons ? productAddons.getSelectedAddons() : [];

        if (addons.length === 0) {
          // No add-ons, use original behavior
          super.addSingleProduct(formData, config, evt);
          return;
        }

        // Add main product with add-ons
        this.addProductWithAddons(formData, config, evt, addons);
      }

      /**
       * Add the main product and all selected add-ons
       */
      addProductWithAddons(formData, config, evt, addons) {
        let mainProductResponse = null;
        let lastSuccessfulResponse = null;

        // First add the main product
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleAddError(response, formData);
              return Promise.reject('Main product add failed');
            }

            mainProductResponse = response;
            lastSuccessfulResponse = response;

            // Add all add-ons sequentially
            return this.addAddonsSequentially(addons, 0, lastSuccessfulResponse);
          })
          .then((finalResponse) => {
            // Use the last successful response (has most up-to-date cart state)
            this.handleAddSuccess(finalResponse || mainProductResponse, formData);

            // Clear add-on selections after successful add
            const productAddons = this.getProductAddons();
            if (productAddons) {
              productAddons.clearSelection();
            }
          })
          .catch((e) => {
            console.error('Add to cart with add-ons error:', e);
            // If we have any successful response, still show the cart
            if (lastSuccessfulResponse) {
              this.handleAddSuccess(lastSuccessfulResponse, formData);
            }
          })
          .finally(() => {
            this.resetButtonState(evt);
          });
      }

      /**
       * Add add-ons one by one to get the final cart state
       */
      addAddonsSequentially(addons, index, lastResponse) {
        if (index >= addons.length) {
          return Promise.resolve(lastResponse);
        }

        const addon = addons[index];
        const addonFormData = new FormData();
        addonFormData.append('id', addon.id);
        addonFormData.append('quantity', addon.quantity);

        // Include sections for the last add-on only
        if (index === addons.length - 1 && this.cart) {
          addonFormData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          addonFormData.append('sections_url', window.location.pathname);
        }

        const addonConfig = fetchConfig('javascript');
        addonConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete addonConfig.headers['Content-Type'];
        addonConfig.body = addonFormData;

        return fetch(`${routes.cart_add_url}`, addonConfig)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // Add-on failed, continue with next
              console.warn('Add-on failed to add:', addon.id, response);
              return this.addAddonsSequentially(addons, index + 1, lastResponse);
            }
            // Success, continue with next add-on
            return this.addAddonsSequentially(addons, index + 1, response);
          })
          .catch((e) => {
            console.warn('Add-on fetch error:', addon.id, e);
            return this.addAddonsSequentially(addons, index + 1, lastResponse);
          });
      }

      /**
       * Override addProductsWithGiftBox to also handle add-ons
       */
      addProductsWithGiftBox(formData, giftBoxProductId, config, evt) {
        const productAddons = this.getProductAddons();
        const addons = productAddons ? productAddons.getSelectedAddons() : [];

        if (addons.length === 0) {
          // No add-ons, use original behavior
          super.addProductsWithGiftBox(formData, giftBoxProductId, config, evt);
          return;
        }

        // Handle gift box + add-ons together
        let mainProductResponse = null;
        let lastSuccessfulResponse = null;

        // First add the main product
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleAddError(response, formData);
              return Promise.reject('Main product add failed');
            }

            mainProductResponse = response;
            lastSuccessfulResponse = response;

            // Add gift box
            const giftBoxFormData = new FormData();
            giftBoxFormData.append('id', giftBoxProductId);
            giftBoxFormData.append('quantity', 1);

            const giftBoxConfig = fetchConfig('javascript');
            giftBoxConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete giftBoxConfig.headers['Content-Type'];
            giftBoxConfig.body = giftBoxFormData;

            return fetch(`${routes.cart_add_url}`, giftBoxConfig);
          })
          .then((response) => {
            if (response && response.json) {
              return response.json();
            }
            return response;
          })
          .then((response) => {
            if (response && !response.status) {
              lastSuccessfulResponse = response;
            }
            // Now add all add-ons
            return this.addAddonsSequentially(addons, 0, lastSuccessfulResponse);
          })
          .then((finalResponse) => {
            this.handleAddSuccess(finalResponse || mainProductResponse, formData);

            // Clear add-on selections
            if (productAddons) {
              productAddons.clearSelection();
            }
          })
          .catch((e) => {
            console.error('Add to cart with gift box and add-ons error:', e);
            if (lastSuccessfulResponse) {
              this.handleAddSuccess(lastSuccessfulResponse, formData);
            }
          })
          .finally(() => {
            this.resetButtonState(evt);
          });
      }
    }

    // Redefine the custom element with our extended class
    // Note: We can't actually redefine, so we need to patch the prototype instead
    const originalAddSingleProduct = OriginalProductForm.prototype.addSingleProduct;
    const originalAddProductsWithGiftBox = OriginalProductForm.prototype.addProductsWithGiftBox;

    // Patch addSingleProduct
    OriginalProductForm.prototype.addSingleProduct = function (formData, config, evt) {
      const productAddons = getProductAddons(this);
      const addons = productAddons ? productAddons.getSelectedAddons() : [];

      if (addons.length === 0) {
        originalAddSingleProduct.call(this, formData, config, evt);
        return;
      }

      addProductWithAddons.call(this, formData, config, evt, addons, productAddons);
    };

    // Patch addProductsWithGiftBox
    OriginalProductForm.prototype.addProductsWithGiftBox = function (formData, giftBoxProductId, config, evt) {
      const productAddons = getProductAddons(this);
      const addons = productAddons ? productAddons.getSelectedAddons() : [];

      if (addons.length === 0) {
        originalAddProductsWithGiftBox.call(this, formData, giftBoxProductId, config, evt);
        return;
      }

      addProductWithGiftBoxAndAddons.call(this, formData, giftBoxProductId, config, evt, addons, productAddons);
    };

    // Helper function to get product-addons element
    function getProductAddons(form) {
      const productInfo = form.closest('product-info');
      if (productInfo) {
        return productInfo.querySelector('product-addons');
      }
      return document.querySelector('product-addons');
    }

    // Helper function to add product with add-ons
    function addProductWithAddons(formData, config, evt, addons, productAddons) {
      const self = this;
      let mainProductResponse = null;
      let lastSuccessfulResponse = null;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            self.handleAddError(response, formData);
            return Promise.reject('Main product add failed');
          }

          mainProductResponse = response;
          lastSuccessfulResponse = response;

          return addAddonsSequentially.call(self, addons, 0, lastSuccessfulResponse);
        })
        .then((finalResponse) => {
          self.handleAddSuccess(finalResponse || mainProductResponse, formData);

          if (productAddons) {
            productAddons.clearSelection();
          }
        })
        .catch((e) => {
          console.error('Add to cart with add-ons error:', e);
          if (lastSuccessfulResponse) {
            self.handleAddSuccess(lastSuccessfulResponse, formData);
          }
        })
        .finally(() => {
          self.resetButtonState(evt);
        });
    }

    // Helper function to add product with gift box and add-ons
    function addProductWithGiftBoxAndAddons(formData, giftBoxProductId, config, evt, addons, productAddons) {
      const self = this;
      let mainProductResponse = null;
      let lastSuccessfulResponse = null;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            self.handleAddError(response, formData);
            return Promise.reject('Main product add failed');
          }

          mainProductResponse = response;
          lastSuccessfulResponse = response;

          const giftBoxFormData = new FormData();
          giftBoxFormData.append('id', giftBoxProductId);
          giftBoxFormData.append('quantity', 1);

          const giftBoxConfig = fetchConfig('javascript');
          giftBoxConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete giftBoxConfig.headers['Content-Type'];
          giftBoxConfig.body = giftBoxFormData;

          return fetch(`${routes.cart_add_url}`, giftBoxConfig);
        })
        .then((response) => {
          if (response && response.json) {
            return response.json();
          }
          return response;
        })
        .then((response) => {
          if (response && !response.status) {
            lastSuccessfulResponse = response;
          }
          return addAddonsSequentially.call(self, addons, 0, lastSuccessfulResponse);
        })
        .then((finalResponse) => {
          self.handleAddSuccess(finalResponse || mainProductResponse, formData);

          if (productAddons) {
            productAddons.clearSelection();
          }
        })
        .catch((e) => {
          console.error('Add to cart with gift box and add-ons error:', e);
          if (lastSuccessfulResponse) {
            self.handleAddSuccess(lastSuccessfulResponse, formData);
          }
        })
        .finally(() => {
          self.resetButtonState(evt);
        });
    }

    // Helper function to add add-ons sequentially
    function addAddonsSequentially(addons, index, lastResponse) {
      const self = this;

      if (index >= addons.length) {
        return Promise.resolve(lastResponse);
      }

      const addon = addons[index];
      const addonFormData = new FormData();
      addonFormData.append('id', addon.id);
      addonFormData.append('quantity', addon.quantity);

      if (index === addons.length - 1 && self.cart) {
        addonFormData.append(
          'sections',
          self.cart.getSectionsToRender().map((section) => section.id)
        );
        addonFormData.append('sections_url', window.location.pathname);
      }

      const addonConfig = fetchConfig('javascript');
      addonConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete addonConfig.headers['Content-Type'];
      addonConfig.body = addonFormData;

      return fetch(`${routes.cart_add_url}`, addonConfig)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            console.warn('Add-on failed to add:', addon.id, response);
            return addAddonsSequentially.call(self, addons, index + 1, lastResponse);
          }
          return addAddonsSequentially.call(self, addons, index + 1, response);
        })
        .catch((e) => {
          console.warn('Add-on fetch error:', addon.id, e);
          return addAddonsSequentially.call(self, addons, index + 1, lastResponse);
        });
    }
  }
})();

